import { skipToken, useMutation, useQuery } from '@tanstack/react-query'
import { getValuationApiClient } from 'ClientPortal/src/api/valuationApiClient'
import { getSelectedCustomer } from 'ClientPortal/src/context/SelectedCustomerContext'
import { WithReactQueryOptions } from 'ComponentLibrary'
import { useGlobalMutationErrorHandler } from 'ClientPortal/src/api/useGlobalMutationErrorHandler'
import { Operation } from 'fast-json-patch'
import invariant from 'tiny-invariant'
import { ORDERS_WITH_PLAUSIBILITY_KEY } from './orders'

const PLAUSIBILITY_KEY = 'PLAUSIBILITY'

function useGetPlausibility({ valuationId, ...options }: WithReactQueryOptions<{ valuationId: string | undefined }>) {
    const valuationApiClient = getValuationApiClient()

    const { data, isPending } = useQuery({
        queryKey: [PLAUSIBILITY_KEY, valuationId],
        queryFn: valuationId
            ? () => valuationApiClient.plausibility.getPlausibility({ valuationId, customerId: getSelectedCustomer()?.uuid })
            : skipToken,
        ...options,
    })

    return { plausibility: data, isPlausibilityPending: isPending }
}

function usePatchPlausibility({ onSuccess, onError, valuationId }: WithReactQueryOptions<{ valuationId?: string }>) {
    const valuationApiClient = getValuationApiClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const { isPending, mutate } = useMutation({
        mutationFn: (operations: Operation[]) => {
            invariant(valuationId, 'valuationId must be defined when calling patchPlausibility')
            return valuationApiClient.plausibility.patchPlausibility({
                valuationId,
                customerId: getSelectedCustomer()?.uuid,
                operations,
            })
        },
        onSuccess,
        onError: handleError,
        meta: {
            invalidates: [[PLAUSIBILITY_KEY], [ORDERS_WITH_PLAUSIBILITY_KEY]],
        },
    })

    return {
        isPatchingPlausibility: isPending,
        patchPlausibility: mutate,
    }
}

export { useGetPlausibility, usePatchPlausibility }
